import React, { useEffect, useState } from "react"
import { graphql, Link, useStaticQuery } from "gatsby"
import { GatsbyImage, getImage } from "gatsby-plugin-image"
import styled from "styled-components"

const INTERVAL_MS = 6500

export const Header = () => {
  const data = useStaticQuery(graphql`
    query FeaturedPosts {
      posts: allMdx(sort: { frontmatter: { date: DESC } }, limit: 5) {
        nodes {
          frontmatter {
            slug
            title
            excerpt
            date(formatString: "D MMMM YYYY")
            featureImage {
              childrenImageSharp {
                gatsbyImageData(
                  width: 1400
                  placeholder: BLURRED
                  transformOptions: { fit: COVER, cropFocus: CENTER }
                )
              }
            }
          }
        }
      }
    }
  `)

  const slides = data.posts.nodes
    .map(node => {
      const img = node.frontmatter.featureImage?.childrenImageSharp?.[0]?.gatsbyImageData
      return {
        slug: node.frontmatter.slug,
        title: node.frontmatter.title,
        excerpt: node.frontmatter.excerpt,
        date: node.frontmatter.date,
        image: img ? getImage(img) : null,
      }
    })
    .filter(item => item.slug && item.title)

  const [index, setIndex] = useState(0)
  const [paused, setPaused] = useState(false)

  useEffect(() => {
    if (paused || slides.length < 2) return
    if (
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ) {
      return
    }
    const id = window.setInterval(() => {
      setIndex(i => (i + 1) % slides.length)
    }, INTERVAL_MS)
    return () => window.clearInterval(id)
  }, [paused, slides.length])

  const slide = slides[index]

  return (
    <Hero>
      <Eyebrow>Anteckningar</Eyebrow>
      <Title>Kod, servrar och hur saker sitter ihop.</Title>
      <Lead>
        Jag skriver om .NET, moderna ramverk och webbdesign. Korta
        handledningar från verkliga projekt.
      </Lead>
      {slide ? (
        <Carousel
          aria-roledescription="karusell"
          aria-label="Utvalda inlägg"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
        >
          <Slide to={`/${slide.slug}`}>
            {slide.image ? <Cover image={slide.image} alt="" /> : <CoverFallback />}
            <Caption>
              <p>
                Utvalt · {slide.date}
              </p>
              <h2>{slide.title}</h2>
              <span>{slide.excerpt}</span>
              <em>Läs mer</em>
            </Caption>
          </Slide>
          {slides.length > 1 ? (
            <Controls>
              <Dots role="tablist" aria-label="Välj inlägg">
                {slides.map((item, i) => (
                  <Dot
                    key={item.slug}
                    type="button"
                    role="tab"
                    aria-selected={i === index}
                    aria-label={`Visa ${item.title}`}
                    $active={i === index}
                    onClick={() => setIndex(i)}
                  />
                ))}
              </Dots>
              <Arrows>
                <button
                  type="button"
                  aria-label="Föregående inlägg"
                  onClick={() => setIndex(i => (i - 1 + slides.length) % slides.length)}
                >
                  ‹
                </button>
                <button
                  type="button"
                  aria-label="Nästa inlägg"
                  onClick={() => setIndex(i => (i + 1) % slides.length)}
                >
                  ›
                </button>
              </Arrows>
            </Controls>
          ) : null}
        </Carousel>
      ) : null}
    </Hero>
  )
}

const Hero = styled.header`
  grid-column: 1 / -1;
  width: min(80rem, calc(100% - 3rem));
  margin: 0 auto;
  padding: 2.5rem 0 0.5rem;
`

const Eyebrow = styled.p`
  font-size: 0.75rem;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--muted) !important;
`

const Title = styled.h1`
  margin-top: 0.7rem;
  max-width: 18ch;
  font-family: var(--font-serif);
  font-size: clamp(2.05rem, 4.2vw, 2.85rem);
  font-weight: 600;
  letter-spacing: -0.02em;
  line-height: 1.18;
`

const Lead = styled.p`
  margin-top: 1rem;
  max-width: 42ch;
  color: var(--muted);
  font-size: 1.05rem;
  line-height: 1.65;
`

const Carousel = styled.section`
  margin-top: 2rem;
`

const Slide = styled(Link)`
  display: grid;
  overflow: hidden;
  border: 1px solid var(--border);
  background: var(--elevated);
  text-decoration: none;
  color: inherit;

  @media ${props => props.theme.breakpoints.desktop} {
    grid-template-columns: minmax(0, 1.45fr) minmax(18rem, 1fr);
  }

  &:hover h2 {
    color: var(--accent);
  }
`

const Cover = styled(GatsbyImage)`
  display: block;
  aspect-ratio: 16 / 9;
  width: 100%;
  height: 100%;

  img {
    object-fit: cover;
    border-radius: 0;
  }

  @media ${props => props.theme.breakpoints.desktop} {
    aspect-ratio: auto;
    min-height: 22rem;
  }
`

const CoverFallback = styled.div`
  min-height: 16rem;
  background: color-mix(in srgb, var(--fg) 6%, transparent);
`

const Caption = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 1.6rem 1.6rem 1.8rem;

  p {
    font-size: 0.75rem;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: var(--muted) !important;
  }

  h2 {
    margin-top: 0.7rem;
    font-size: clamp(1.6rem, 3vw, 2.15rem);
    font-weight: 600;
    letter-spacing: -0.02em;
    line-height: 1.18;
  }

  span {
    display: block;
    margin-top: 0.75rem;
    max-width: 38ch;
    color: var(--muted);
    font-size: 1.02rem;
    line-height: 1.65;
  }

  em {
    margin-top: 1.15rem;
    font-style: normal;
    font-size: 0.875rem;
    color: var(--accent);
  }
`

const Controls = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-top: 1rem;
`

const Dots = styled.div`
  display: flex;
  gap: 0.5rem;
`

const Dot = styled.button`
  width: 1.5rem;
  height: 0.5rem;
  border: 0;
  background: ${props => (props.$active ? "var(--accent)" : "color-mix(in srgb, var(--fg) 20%, transparent)")};
`

const Arrows = styled.div`
  display: flex;

  button {
    width: 2.5rem;
    height: 2.5rem;
    border: 0;
    background: transparent;
    color: var(--fg);
    font-size: 1.6rem;
    line-height: 1;
  }
`
